import { Course } from '../types/course';
import { MetrixService } from './metrixService';
import { MetrixCompetition } from '../types/metrix';

/**
 * Service responsible for fetching and managing course and competition data
 * This is a singleton to ensure consistent data across the application
 */
export class CourseDataService {
  private static instance: CourseDataService;
  private courses: Course[] = [];
  private competitions: any[] = [];
  private isLoading: boolean = false;
  private isInitialized: boolean = false;
  private dataLoadPromise: Promise<void> | null = null;

  private constructor() {
    // Private constructor to prevent direct instantiation
  }

  /**
   * Get the singleton instance of CourseDataService
   */
  public static getInstance(): CourseDataService {
    if (!CourseDataService.instance) {
      CourseDataService.instance = new CourseDataService();
    }
    return CourseDataService.instance;
  }

  /**
   * Initialize course and competition data
   * Returns a promise that resolves when data is loaded
   */
  public async initialize(): Promise<void> {
    // If already initialized or loading, return existing promise
    if (this.isInitialized) return Promise.resolve();
    if (this.isLoading && this.dataLoadPromise) return this.dataLoadPromise;

    this.isLoading = true;
    // Loading course and competition data

    // Create a new promise for data loading
    this.dataLoadPromise = new Promise<void>((resolve, reject) => {
      try {
        // Check if window globals exist
        if (window.coursesData && window.coursesData.length > 0) {
          // Using window.coursesData
          this.courses = window.coursesData;
          this.competitions = window.competitionsData || [];
          this.isInitialized = true;
          this.isLoading = false;
          resolve();
          return;
        }

        // If window globals don't exist, load data from Metrix API
        // window.coursesData not found, loading from API
        
        // Tarkista onko URL:ssa forceReload=true
        const urlParams = new URLSearchParams(window.location.search);
        const forceReload = urlParams.get('forceReload') === 'true';
        
        if (forceReload) {
          // Force reload request detected - bypassing cache
          localStorage.removeItem('ratahaukka_courses_data');
          localStorage.removeItem('ratahaukka_competitions_data');
        } else {
          // Attempt to get data from localStorage cache
          const cachedCoursesStr = localStorage.getItem('ratahaukka_courses_data');
          const cachedCompetitionsStr = localStorage.getItem('ratahaukka_competitions_data');
          
          if (cachedCoursesStr) {
            try {
              const parsedCourses = JSON.parse(cachedCoursesStr);
              // Tarkista että välimuistissa on riittävästi ratoja (vähintään 100 rataa)
              // Jos vähemmän, pakotetaan lataus Firestoresta
              if (Array.isArray(parsedCourses) && parsedCourses.length >= 100) {
                // Using cached courses data
                this.courses = parsedCourses;
                
                if (cachedCompetitionsStr) {
                  const parsedCompetitions = JSON.parse(cachedCompetitionsStr);
                  if (Array.isArray(parsedCompetitions)) {
                    this.competitions = parsedCompetitions;
                  }
                }
                
                // Sync to window globals for compatibility
                window.coursesData = this.courses;
                window.competitionsData = this.competitions;
                
                this.isInitialized = true;
                this.isLoading = false;
                resolve();
                return;
              } else {
                // Not enough courses in cache, loading from Firestore
              }
            } catch (e) {
              console.warn('CourseDataService: Failed to parse cached data', e);
              // Continue with API fetch
            }
          }
        }
        
        // Get course data from the API or local cache
        this.fetchCoursesFromApi().then(courses => {
          return Promise.all([
            Promise.resolve(courses),
            this.fetchCompetitionsFromApi()
          ])
        }).then(([courses, competitions]) => {
          this.courses = courses;
          this.competitions = competitions;
          
          // Save to window globals for compatibility with both V1 and V2
          window.coursesData = this.courses;
          window.competitionsData = this.competitions;
          
          // Cache data in localStorage
          try {
            localStorage.setItem('ratahaukka_courses_data', JSON.stringify(this.courses));
            localStorage.setItem('ratahaukka_competitions_data', JSON.stringify(this.competitions));
          } catch (e) {
            console.warn('CourseDataService: Failed to cache data in localStorage', e);
          }
          
          this.isInitialized = true;
          this.isLoading = false;
          resolve();
        }).catch(error => {
          console.error('CourseDataService: Error fetching data:', error);
          this.isLoading = false;
          reject(error);
        });
      } catch (error) {
        console.error('CourseDataService: Initialization error:', error);
        this.isLoading = false;
        reject(error);
      }
    });

    return this.dataLoadPromise;
  }

  /**
   * Get all courses
   */
  public getCourses(): Course[] {
    return this.courses;
  }

  /**
   * Get all competitions
   */
  public getCompetitions(): any[] {
    return this.competitions;
  }

  /**
   * Check if data is still loading
   */
  public isDataLoading(): boolean {
    return this.isLoading;
  }

  /**
   * Fetch courses from API or fallback sources
   * This method attempts to fetch course data from multiple sources
   */
  private async fetchCoursesFromApi(): Promise<Course[]> {
    try {
      // Fetching courses directly from Firestore - all caching disabled
      
      // Tyhjennä kaikki välimuistit varmuuden vuoksi
      localStorage.removeItem('ratahaukka_courses_data');
      localStorage.removeItem('ratahaukka_competitions_data');
      // Alusta tyhjät taulukot TypeScript-yhteensopivasti
      window.coursesData = [];
      window.competitionsData = [];
      
      // Pakota lataus suoraan Firestoresta
      // Forcing direct Firestore load - bypassing all caches
      const metrixService = MetrixService.getInstance();
      
      // Pakota MetrixService hakemaan suoraan Firestoresta
      // Käytä forcedFetch-parametria ohittamaan kaikki välimuistit
      const courses = await metrixService.fetchMetrixCourses(true); // true = force direct Firestore fetch
      
      // Tarkista tulokset
      if (courses && courses.length > 0) {
        // Successfully fetched courses from Firestore
        
        // Välimuistia EI päivitetä ennen kuin toiminnallisuus on korjattu
        // window.coursesData = courses; // kommentoitu pois
        
        return courses;
      }
      
      console.error('CourseDataService: Failed to fetch any courses from Firestore');
      return [];
    } catch (error) {
      console.error('CourseDataService: Error fetching courses:', error);
      return [];
    }
  }

  /**
   * Fetch competitions directly from Firestore
   */
  private async fetchCompetitionsFromApi(): Promise<MetrixCompetition[]> {
    try {
      // Fetching competitions from Firestore
      
      // Get window data if available
      if (window.competitionsData && window.competitionsData.length > 0) {
        // Using window.competitionsData
        return window.competitionsData;
      }

      // If no window data, try to load from localStorage cache
      const cachedDataStr = localStorage.getItem('ratahaukka_competitions_data');
      if (cachedDataStr) {
        try {
          const parsedData = JSON.parse(cachedDataStr);
          if (Array.isArray(parsedData) && parsedData.length > 0) {
            // Using cached competitions data from localStorage
            return parsedData;
          }
        } catch (e) {
          console.warn('CourseDataService: Error parsing cached competitions data', e);
        }
      }

      // Fetch competitions directly from Firestore
      // Fetching competitions directly from Firestore database
      const metrixService = MetrixService.getInstance();
      const startDate = new Date();
      const endDate = new Date();
      // Fetch competitions for a full year
      endDate.setFullYear(endDate.getFullYear() + 1);
      
      const competitions = await metrixService.fetchMetrixCompetitions({ startDate, endDate });
      
      if (competitions && competitions.length > 0) {
        // Successfully fetched competitions from Firestore
        return competitions;
      }
      
      console.warn('CourseDataService: No competitions found in Firestore');
      return [];
    } catch (error) {
      console.error('CourseDataService: Error fetching competitions', error);
      return [];
    }
  }
}
